import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  EntitiesStore,
  EntityStore,
} from '@madeinlune/massaud-json-api-bridge';
import { Page, UiArticleGridItem } from '@madeinlune/massaud-types';
import { combineLatest, map, Observable } from 'rxjs';
import { LetDirective } from '@ngrx/component';
import { ActivatedRoute } from '@angular/router';
import { PromotedNewsComponent } from '@nx-massaud/detail/ui/promoted-news';
import { LoadingDirective } from '@nx-massaud/loading';
import { RoutedComponent, RoutedComponentError } from '@nx-massaud/ui';
import { ComponentStore } from '@ngrx/component-store';
import { GridListComponent } from '@nx-massaud/ui/grid';
import {
  articleListQuery,
  pageDetailQuery,
} from '@madeinlune/massaud-query-builder';

@Component({
  selector: 'msd-det-deat-news',
  standalone: true,
  imports: [
    CommonModule,
    LetDirective,
    PromotedNewsComponent,
    LoadingDirective,
    GridListComponent,
  ],
  template: `
    <ng-container *ngrxLet="vm$; let vm">
      <ng-container *ngIf="vm.promotedArticle as promotedArticle">
        <msd-det-ui-promoted-news
          [news]="promotedArticle"
        ></msd-det-ui-promoted-news>
      </ng-container>
      <ng-container *ngIf="vm.page as page">
        <h1 [innerHTML]="page.title"></h1>
      </ng-container>
      <msd-ui-grid-list [data]="vm.articles"></msd-ui-grid-list>
    </ng-container>
  `,
  styleUrls: ['./news.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EntityStore, EntitiesStore],
})
export class NewsComponent
  extends ComponentStore<any>
  implements RoutedComponent
{
  private articlesStore = inject(EntitiesStore<UiArticleGridItem>);

  private readonly page$ = this.newsStore.entity$;
  private readonly articlesLoading$ = this.articlesStore.loading$;

  private readonly articles$ = this.articlesStore.entities$.pipe(
    map((articles) => {
      if (articles) {
        return articles.filter((a) => !a.promote);
      }
      return undefined;
    })
  );

  private readonly promotedArticle$ = this.articlesStore.entities$.pipe(
    map((articles) => {
      if (articles) {
        return articles.find((a) => a.promote);
      }
      return undefined;
    })
  );

  readonly overallRichError$: Observable<RoutedComponentError | undefined> =
    combineLatest([
      this.articlesStore.errorMessage$,
      this.articlesStore.error$,
    ]).pipe(
      map(([articlesErrorMessage, articlesError]) => {
        const message = articlesErrorMessage;
        if (message) {
          const errors = [];
          if (articlesError) {
            errors.push(articlesError);
          }
          return { message, errors };
        }
        return undefined;
      })
    );
  readonly overallLoading$ = combineLatest([
    this.articlesStore.initialSuccess$,
  ]).pipe(
    map(([articlesSuccess]) => {
      return !!!articlesSuccess;
    })
  );

  vm$ = this.select({
    page: this.page$,
    articles: this.articles$,
    promotedArticle: this.promotedArticle$,
  });

  constructor(
    private newsStore: EntityStore<Page>,
    activatedRouter: ActivatedRoute
  ) {
    super({});
    this.articlesStore.modelClass = UiArticleGridItem;
    this.newsStore.load({
      query: pageDetailQuery,
      entityId: activatedRouter.snapshot.params['pageId'],
    });
    this.articlesStore.load({ query: articleListQuery });
  }
}
